/**
 * Sources Library — visual grid of every YouTube podcast we've ever
 * clipped. Built so the operator can skip the "open YouTube tab → copy
 * URL → copy speaker name → paste into New Job" loop when starting a
 * second / third / fourth clip from the same podcast.
 *
 * Server-side aggregation: GET /api/sources walks work/*\/{source,input}.json,
 * groups by canonical YouTube video id, returns one row per podcast. No
 * persistence layer — rebuilt on every request (cheap; ~dozens of dirs).
 */
import { Bookmark, BookmarkCheck, Check, Copy, ExternalLink, Loader2, Pencil, RefreshCw, ScanSearch, Search, Send, Users, Video } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createScan, editSourceSpeaker, listSources, listDiscoverSources, setSourceBookmark, type BacklogDraft, type DiscoverSource, type Source } from "../api";
import { PageHeader } from "../components/PageHeader";
import { useIsMobile } from "../lib/useIsMobile";

interface Props {
  /** Pre-fill the New Job form with this source's URL + speaker info. */
  onUseInNewJob: (draft: BacklogDraft) => void;
}

/** Map any source (mine or shared) to a New Job pre-fill draft. */
function sourceToDraft(s: Source): BacklogDraft {
  return {
    url: s.url,
    from: "",
    host: s.hosts[0] ?? "",
    guests: s.guests,
    topic: s.topics[0] ?? "",
    label: s.title ?? "",
    notes: "",
  };
}

function fmtRelative(ts: number): string {
  const dt = Date.now() - ts;
  if (dt < 60_000) return "just now";
  if (dt < 3_600_000) return `${Math.round(dt / 60_000)}m ago`;
  if (dt < 86_400_000) return `${Math.round(dt / 3_600_000)}h ago`;
  if (dt < 7 * 86_400_000) return `${Math.round(dt / 86_400_000)}d ago`;
  if (dt < 30 * 86_400_000) return `${Math.round(dt / (7 * 86_400_000))}w ago`;
  return `${Math.round(dt / (30 * 86_400_000))}mo ago`;
}

function speakerLine(s: Source): string {
  // What goes on the clipboard line under the URL. Prefer "host with
  // guest1, guest2" since that's how podcasts are credited.
  const host = s.hosts[0];
  const guests = s.guests.length > 0 ? s.guests.join(", ") : "";
  if (host && guests) return `${host} with ${guests}`;
  if (host) return host;
  if (guests) return guests;
  return "";
}

type SourcesTab = "mine" | "discover";
type DiscoverFilter = "all" | "saved" | "new";

function gridStyle(isMobile: boolean): React.CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 16,
  };
}

export function Sources({ onUseInNewJob }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<SourcesTab>("mine");
  const [mine, setMine] = useState<Source[]>([]);
  // null = shared Discovery feature is off (V1 or kill-switch) → no Discover tab.
  const [discover, setDiscover] = useState<DiscoverSource[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [discFilter, setDiscFilter] = useState<DiscoverFilter>("all");
  const [bmBusy, setBmBusy] = useState<Record<string, boolean>>({});
  const [scanState, setScanState] = useState<Record<string, "busy" | "done">>({});

  const refresh = async (): Promise<void> => {
    setRefreshing(true);
    setError(null);
    try {
      const [m, d] = await Promise.all([listSources(), listDiscoverSources()]);
      setMine(m);
      setDiscover(d ? d.items : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const discoverOn = discover !== null;
  const activeTab: SourcesTab = discoverOn ? tab : "mine";

  // Videos this channel saved but hasn't clipped — shown as a shortlist strip
  // atop My Sources so a bookmark reads as "added to my sources".
  const savedNotClipped = useMemo(
    () => (discover ?? []).filter((d) => d.bookmarkedByActive && !d.clippedByActive),
    [discover],
  );
  const bookmarkedIds = useMemo(
    () => new Set((discover ?? []).filter((d) => d.bookmarkedByActive).map((d) => d.videoId)),
    [discover],
  );

  const toggleBookmark = async (videoId: string, on: boolean): Promise<void> => {
    setBmBusy((b) => ({ ...b, [videoId]: true }));
    try {
      await setSourceBookmark(videoId, on);
      setDiscover((prev) =>
        prev?.map((d) => (d.videoId === videoId ? { ...d, bookmarkedByActive: on } : d)) ?? prev,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBmBusy((b) => { const n = { ...b }; delete n[videoId]; return n; });
    }
  };

  const startScan = async (videoId: string, url: string): Promise<void> => {
    setScanState((s) => ({ ...s, [videoId]: "busy" }));
    try {
      await createScan(url);
      setScanState((s) => ({ ...s, [videoId]: "done" }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setScanState((s) => { const n = { ...s }; delete n[videoId]; return n; });
    }
  };

  const mineFiltered = useMemo(() => searchAndRank(mine, query), [mine, query]);
  const discoverFiltered = useMemo(() => {
    let list: DiscoverSource[] = discover ?? [];
    if (discFilter === "saved") list = list.filter((d) => d.bookmarkedByActive);
    else if (discFilter === "new") list = list.filter((d) => !d.clippedByActive && !d.scannedByActive);
    return searchAndRank(list, query) as DiscoverSource[];
  }, [discover, discFilter, query]);

  const shown = activeTab === "mine" ? mineFiltered.length : discoverFiltered.length;
  const total = activeTab === "mine" ? mine.length : (discover?.length ?? 0);

  return (
    <div>
      <PageHeader
        eyebrow="Sources"
        title={discoverOn ? "Your podcasts — and the shared library." : "Every podcast you've clipped, in one library."}
        subtitle={discoverOn
          ? "My Sources is what this channel has clipped. Discover is the shared pool every channel feeds — scan, clip, or bookmark any of it for this channel."
          : "Auto-built from your job history — each card is a unique YouTube source. One click to copy the URL + speaker, or to spawn a new clip from the same podcast."}
        action={
          <button
            type="button"
            onClick={() => void refresh()}
            title="Rebuild from work/"
            style={{
              width: 36, height: 36, borderRadius: 10,
              border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.02)",
              color: "var(--ql-fg-muted)", cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <RefreshCw size={15} strokeWidth={2} className={refreshing ? "ql-spin" : undefined} />
          </button>
        }
      />

      {discoverOn && (
        <div style={{ display: "flex", gap: 6, padding: 5, marginBottom: 18, borderRadius: 12, background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)", width: "fit-content" }}>
          <TabButton active={activeTab === "mine"} onClick={() => setTab("mine")} icon={<Video size={14} />} label="My Sources" count={mine.length} />
          <TabButton active={activeTab === "discover"} onClick={() => setTab("discover")} icon={<Users size={14} />} label="Discover" count={discover?.length ?? 0} />
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: activeTab === "discover" ? 14 : 22, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 240, maxWidth: 420 }}>
          <Search size={14} strokeWidth={2} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ql-fg-subtle)", pointerEvents: "none" }} />
          <input
            type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, host, guest, topic…"
            style={{
              width: "100%", padding: "9px 12px 9px 34px", fontSize: 13, fontFamily: "inherit",
              borderRadius: 10, border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.02)",
              color: "var(--ql-fg)", outline: "none",
            }}
          />
        </div>
        <span style={{ fontSize: 12, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)" }}>
          {shown} of {total}
        </span>
      </div>

      {activeTab === "discover" && (
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <FilterChip active={discFilter === "all"} onClick={() => setDiscFilter("all")}>All</FilterChip>
          <FilterChip active={discFilter === "saved"} onClick={() => setDiscFilter("saved")}>★ Saved</FilterChip>
          <FilterChip active={discFilter === "new"} onClick={() => setDiscFilter("new")}>New to this channel</FilterChip>
        </div>
      )}

      {error && (
        <div role="alert" style={{ margin: "0 0 18px", padding: "10px 14px", borderRadius: 12, background: "rgba(248,113,113,.10)", border: "1px solid rgba(248,113,113,.30)", color: "#FCA5A5", fontSize: 13 }}>
          {error}
        </div>
      )}

      {!loaded ? (
        <SkeletonGrid />
      ) : activeTab === "mine" ? (
        mine.length === 0 && savedNotClipped.length === 0 ? (
          <EmptyHero />
        ) : (
          <div style={{ display: "grid", gap: 24 }}>
            {savedNotClipped.length > 0 && (
              <div>
                <SectionLabel icon={<BookmarkCheck size={13} />}>Saved to work on ({savedNotClipped.length})</SectionLabel>
                <div style={gridStyle(isMobile)}>
                  {savedNotClipped.map((s) => (
                    <DiscoverCard
                      key={s.videoId} source={s}
                      bookmarking={!!bmBusy[s.videoId]} scanState={scanState[s.videoId]}
                      onToggleBookmark={() => void toggleBookmark(s.videoId, !s.bookmarkedByActive)}
                      onClip={() => onUseInNewJob(sourceToDraft(s))}
                      onScan={() => void startScan(s.videoId, s.url)}
                    />
                  ))}
                </div>
              </div>
            )}
            {mineFiltered.length === 0 ? (
              mine.length === 0
                ? <div style={emptyNoteStyle}>Nothing clipped yet — bookmark videos from Discover to build a shortlist.</div>
                : <div style={emptyNoteStyle}>No sources match "{query}".</div>
            ) : (
              <div>
                {savedNotClipped.length > 0 && <SectionLabel icon={<Video size={13} />}>Clipped by this channel</SectionLabel>}
                <div style={gridStyle(isMobile)}>
                  {mineFiltered.map((s) => (
                    <SourceCard
                      key={s.videoId} source={s} onEdited={refresh}
                      onUseInNewJob={() => onUseInNewJob(sourceToDraft(s))}
                      bookmarked={discoverOn ? bookmarkedIds.has(s.videoId) : undefined}
                      bookmarking={!!bmBusy[s.videoId]}
                      onToggleBookmark={discoverOn ? () => void toggleBookmark(s.videoId, !bookmarkedIds.has(s.videoId)) : undefined}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      ) : (discover?.length ?? 0) === 0 ? (
        <DiscoverEmptyHero />
      ) : discoverFiltered.length === 0 ? (
        <div style={emptyNoteStyle}>
          {discFilter === "saved" ? "No saved sources yet — hit the bookmark on any card." : `No sources match "${query}".`}
        </div>
      ) : (
        <div style={gridStyle(isMobile)}>
          {discoverFiltered.map((s) => (
            <DiscoverCard
              key={s.videoId} source={s}
              bookmarking={!!bmBusy[s.videoId]} scanState={scanState[s.videoId]}
              onToggleBookmark={() => void toggleBookmark(s.videoId, !s.bookmarkedByActive)}
              onClip={() => onUseInNewJob(sourceToDraft(s))}
              onScan={() => void startScan(s.videoId, s.url)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const emptyNoteStyle: React.CSSProperties = {
  padding: "40px 24px", textAlign: "center", border: "1px dashed rgba(255,255,255,.08)",
  borderRadius: 16, color: "var(--ql-fg-muted)", fontSize: 13,
};

function TabButton({ active, onClick, icon, label, count }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; count: number }): React.JSX.Element {
  return (
    <button type="button" onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 9,
      fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer",
      border: "1px solid " + (active ? "rgba(255,213,0,.35)" : "transparent"),
      background: active ? "rgba(255,213,0,.10)" : "transparent",
      color: active ? "var(--ql-fg)" : "var(--ql-fg-muted)",
    }}>
      <span style={{ color: active ? "var(--ql-yellow)" : "var(--ql-fg-subtle)", display: "inline-flex" }}>{icon}</span>
      {label}
      <span style={{ fontSize: 11, fontFamily: "var(--ql-font-mono)", color: "var(--ql-fg-subtle)" }}>{count}</span>
    </button>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }): React.JSX.Element {
  return (
    <button type="button" onClick={onClick} style={{
      padding: "6px 12px", borderRadius: 999, fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer",
      border: "1px solid " + (active ? "rgba(255,213,0,.35)" : "rgba(255,255,255,.10)"),
      background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
      color: active ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
    }}>{children}</button>
  );
}

function SectionLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--ql-fg-subtle)" }}>
      <span style={{ display: "inline-flex", color: "var(--ql-yellow)" }}>{icon}</span>
      {children}
    </div>
  );
}

/**
 * Token-AND, field-weighted ranker over the Sources list.
 *
 *  - Query is split on whitespace; every token must hit SOMEWHERE in the
 *    source's searchable fields (title, hosts, guests, topics, URL,
 *    video id, job slugs). "robert greene art" matches a source where
 *    "Robert Greene" is the guest and "Art" is in the title — the old
 *    single-substring match couldn't span fields.
 *  - Field weights: speakers (host/guest) and title outweigh topic, URL
 *    is a last-resort match (people paste links in search). So if you
 *    type a guest name and they have one source matching by name and
 *    another by URL-only, the name match comes first.
 *  - Empty query short-circuits to the default lastUsedAt sort from the
 *    server, so the list looks the same as before until the user types.
 *
 * Pure & exported so we can unit-test it without a React harness when
 * the rule set grows.
 */
export function searchAndRank(items: Source[], query: string): Source[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return items;

  const FIELD_WEIGHTS = {
    title: 4,
    speakers: 5, // host + guest — the most common "I remember the person, not the title" query
    topics: 3,
    url: 1,
    videoId: 2,
    jobIds: 1,
  } as const;

  const scored = items
    .map((s) => {
      const fields = {
        title: (s.title ?? "").toLowerCase(),
        speakers: [...s.hosts, ...s.guests].join("   ").toLowerCase(),
        topics: s.topics.join("   ").toLowerCase(),
        url: s.url.toLowerCase(),
        videoId: s.videoId.toLowerCase(),
        jobIds: s.jobIds.join(" ").toLowerCase(),
      };
      let totalScore = 0;
      for (const tok of tokens) {
        let tokenHitsAnyField = false;
        for (const [field, weight] of Object.entries(FIELD_WEIGHTS) as Array<[
          keyof typeof FIELD_WEIGHTS,
          number,
        ]>) {
          if (fields[field].includes(tok)) {
            totalScore += weight;
            tokenHitsAnyField = true;
          }
        }
        // AND semantics — if any single token can't be found anywhere,
        // this source is out. Otherwise users typing a long sentence
        // get a flood of partial matches, which feels broken.
        if (!tokenHitsAnyField) return null;
      }
      return { source: s, score: totalScore };
    })
    .filter((row): row is { source: Source; score: number } => row !== null);

  // Higher score first; ties broken by lastUsedAt (the default sort).
  scored.sort((a, b) =>
    b.score - a.score || b.source.lastUsedAt - a.source.lastUsedAt,
  );
  return scored.map((r) => r.source);
}

function SourceCard({
  source,
  onUseInNewJob,
  onEdited,
  bookmarked,
  bookmarking,
  onToggleBookmark,
}: {
  source: Source;
  onUseInNewJob: () => void;
  onEdited: () => void | Promise<void>;
  /** Bookmark state for the active channel (undefined = Discovery off → no star). */
  bookmarked?: boolean;
  bookmarking?: boolean;
  onToggleBookmark?: () => void;
}): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  // Inline speaker-name correction. Applies to every clip of this source
  // (card-only re-render each) and re-labels Sources + Library. On success we
  // ask the parent to re-fetch so the corrected name shows immediately.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(source.hosts[0] ?? "");
  const [savingName, setSavingName] = useState(false);
  const [nameErr, setNameErr] = useState<string | null>(null);
  const nameChanged = nameDraft.trim() !== "" && nameDraft.trim() !== (source.hosts[0] ?? "").trim();

  const openNameEditor = (): void => {
    setNameDraft(source.hosts[0] ?? "");
    setNameErr(null);
    setEditingName(true);
  };
  const saveName = async (): Promise<void> => {
    if (savingName || !nameChanged) return;
    setSavingName(true);
    setNameErr(null);
    try {
      await editSourceSpeaker(source.videoId, nameDraft.trim());
      setEditingName(false);
      setSavingName(false);
      // The corrected name is written synchronously before the re-render spawns,
      // so a re-fetch shows it immediately.
      await onEdited();
    } catch (err) {
      setNameErr(err instanceof Error ? err.message : String(err));
      setSavingName(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    // Two-line clipboard payload: URL on the first line, "host with
    // guest" on the second. Paste anywhere and the URL is the first
    // line; tools that auto-link (Discord/Slack/Notion) pick it up.
    const payload = [source.url, speakerLine(source)].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1300);
    } catch {
      /* clipboard blocked — silent */
    }
  };

  const speakers = speakerLine(source);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 14,
        background: "rgba(16,18,27,.55)",
        border: "1px solid rgba(255,255,255,.06)",
        overflow: "hidden",
        transition: "all .15s var(--ql-ease)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,213,0,.20)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,.06)";
      }}
    >
      {/* Thumbnail (16:9 — YouTube's native aspect for hqdefault) */}
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          background: thumbFailed
            ? "linear-gradient(135deg, rgba(255,213,0,.12), rgba(16,18,27,.9))"
            : "rgba(0,0,0,.5)",
          overflow: "hidden",
        }}
      >
        {!thumbFailed ? (
          <img
            src={source.thumbnailUrl}
            alt={source.title ?? "Source thumbnail"}
            loading="lazy"
            onError={() => setThumbFailed(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--ql-yellow)",
            }}
          >
            <Video size={32} strokeWidth={1.5} />
          </div>
        )}
        {/* Clip count badge */}
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            background: "rgba(0,0,0,.65)",
            border: "1px solid rgba(255,213,0,.30)",
            backdropFilter: "blur(8px)",
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          {source.clipCount} clip{source.clipCount === 1 ? "" : "s"}
        </div>
        {onToggleBookmark && (
          <BookmarkButton bookmarked={!!bookmarked} busy={!!bookmarking} onClick={onToggleBookmark} overlay />
        )}
      </div>

      {/* Body */}
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          title={source.title ?? source.url}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--ql-fg)",
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {source.title || "(untitled YouTube video)"}
        </div>

        {editingName ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void saveName();
                if (e.key === "Escape") { setEditingName(false); setNameErr(null); }
              }}
              placeholder="e.g. Dr Julie Smith"
              aria-label="Speaker name"
              disabled={savingName}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "7px 9px",
                borderRadius: 8,
                background: "rgba(255,255,255,.05)",
                border: "1px solid rgba(255,213,0,.35)",
                color: "var(--ql-fg)",
                fontFamily: "inherit",
                fontSize: 12,
              }}
            />
            {nameErr && (
              <div role="alert" style={{ fontSize: 10, color: "var(--ql-danger)", fontFamily: "var(--ql-font-mono)" }}>{nameErr}</div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => void saveName()}
                disabled={savingName || !nameChanged}
                style={{
                  flex: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  padding: "6px 8px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,213,0,.45)",
                  background: "rgba(255,213,0,.14)",
                  color: "var(--ql-yellow)",
                  fontFamily: "inherit",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: savingName || !nameChanged ? "not-allowed" : "pointer",
                  opacity: savingName || !nameChanged ? 0.5 : 1,
                }}
              >
                {savingName ? <Loader2 size={12} className="ql-spin" /> : <Pencil size={12} strokeWidth={2} />}
                {savingName ? "Saving…" : "Save & re-render"}
              </button>
              <button
                type="button"
                onClick={() => { setEditingName(false); setNameErr(null); }}
                disabled={savingName}
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.15)",
                  background: "transparent",
                  color: "var(--ql-fg-muted)",
                  fontFamily: "inherit",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: savingName ? "not-allowed" : "pointer",
                  opacity: savingName ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <span
              title={speakers || (source.clipCount === 1 ? "No speaker set — click the pencil to add one" : "")}
              style={{
                fontSize: 11,
                color: "var(--ql-fg-muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {speakers || "—"}
            </span>
            {/* Editing is scoped to single-clip sources: a multi-clip source can
                have genuinely different per-clip speakers, so those are corrected
                per-clip in Library, not flattened to one name here. */}
            {source.clipCount === 1 && (
              <span
                role="button"
                tabIndex={0}
                title="Edit speaker name"
                aria-label="Edit speaker name"
                onClick={openNameEditor}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openNameEditor(); }
                }}
                style={{
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "var(--ql-fg-subtle)",
                  padding: 2,
                  borderRadius: 6,
                }}
              >
                <Pencil size={11} strokeWidth={2} />
              </span>
            )}
          </div>
        )}

        <div
          style={{
            fontSize: 10,
            color: "var(--ql-fg-subtle)",
            fontFamily: "var(--ql-font-mono)",
            marginTop: -2,
          }}
        >
          last used {fmtRelative(source.lastUsedAt)}
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <button
            type="button"
            onClick={() => void handleCopy()}
            title="Copy URL + speaker to clipboard"
            style={{
              flex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "8px 10px",
              borderRadius: 10,
              border: copied
                ? "1px solid rgba(74,222,128,.30)"
                : "1px solid rgba(255,255,255,.10)",
              background: copied
                ? "rgba(74,222,128,.10)"
                : "rgba(255,255,255,.03)",
              color: copied ? "#4ADE80" : "var(--ql-fg)",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: "pointer",
              transition: "all .15s var(--ql-ease)",
            }}
          >
            {copied ? <Check size={13} strokeWidth={2.4} /> : <Copy size={13} strokeWidth={2} />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={onUseInNewJob}
            title="Start a new clip from this source"
            style={{
              flex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px solid rgba(255,213,0,.25)",
              background: "rgba(255,213,0,.08)",
              color: "var(--ql-yellow)",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: "pointer",
              transition: "all .15s var(--ql-ease)",
            }}
          >
            <Send size={12} strokeWidth={2.2} /> New clip
          </button>
          <a
            href={source.url}
            target="_blank"
            rel="noreferrer"
            title="Open original on YouTube"
            style={{
              width: 36,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,.08)",
              background: "rgba(255,255,255,.02)",
              color: "var(--ql-fg-muted)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
            }}
          >
            <ExternalLink size={13} strokeWidth={2} />
          </a>
        </div>
      </div>
    </div>
  );
}

/** Star toggle — an overlay pill on a thumbnail, or an inline action button. */
function BookmarkButton({ bookmarked, busy, onClick, overlay }: { bookmarked: boolean; busy: boolean; onClick: () => void; overlay?: boolean }): React.JSX.Element {
  const base: React.CSSProperties = overlay
    ? { position: "absolute", top: 8, left: 8, width: 30, height: 30, borderRadius: 8, background: "rgba(0,0,0,.65)", backdropFilter: "blur(8px)" }
    : { flex: 1, padding: "8px 10px", borderRadius: 10, background: bookmarked ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.03)", gap: 6, fontSize: 12, fontWeight: 700 };
  return (
    <button
      type="button"
      title={bookmarked ? "Remove from this channel's shortlist" : "Save to this channel's shortlist"}
      aria-label={bookmarked ? "Remove bookmark" : "Bookmark"}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={busy}
      style={{
        ...base,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        border: "1px solid " + (bookmarked ? "rgba(255,213,0,.4)" : "rgba(255,255,255,.12)"),
        color: bookmarked ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
        fontFamily: "inherit", cursor: busy ? "default" : "pointer",
      }}
    >
      {busy ? <Loader2 size={14} className="ql-spin" /> : bookmarked ? <BookmarkCheck size={15} strokeWidth={2} /> : <Bookmark size={15} strokeWidth={2} />}
      {!overlay && (bookmarked ? "Saved" : "Save")}
    </button>
  );
}

/** One "who's touched this" pill on a Discover card. */
function ChannelTouchBadge({ touch }: { touch: DiscoverSource["channels"][number] }): React.JSX.Element {
  const label = touch.clips > 0 ? `${touch.clips} clip${touch.clips === 1 ? "" : "s"}` : touch.scanned ? "scanned" : "saved";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px", borderRadius: 999,
      fontSize: 10.5, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", color: "var(--ql-fg-muted)",
    }}>
      {touch.bookmarked && <BookmarkCheck size={10} strokeWidth={2.4} style={{ color: "var(--ql-yellow)" }} />}
      <span style={{ color: "var(--ql-fg)", fontWeight: 600 }}>{touch.displayName}</span>
      <span style={{ color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)" }}>· {label}</span>
    </span>
  );
}

/** A shared-pool card: thumbnail + per-channel touch badges + Scan / Clip / Bookmark. */
function DiscoverCard({ source, bookmarking, scanState, onToggleBookmark, onClip, onScan }: {
  source: DiscoverSource;
  bookmarking: boolean;
  scanState?: "busy" | "done";
  onToggleBookmark: () => void;
  onClip: () => void;
  onScan: () => void;
}): React.JSX.Element {
  const [thumbFailed, setThumbFailed] = useState(false);
  const speakers = speakerLine(source);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", borderRadius: 14, background: "rgba(16,18,27,.55)", border: "1px solid rgba(255,255,255,.06)", overflow: "hidden", transition: "all .15s var(--ql-ease)" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,213,0,.20)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,.06)"; }}
    >
      <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: thumbFailed ? "linear-gradient(135deg, rgba(255,213,0,.12), rgba(16,18,27,.9))" : "rgba(0,0,0,.5)", overflow: "hidden" }}>
        {!thumbFailed ? (
          <img src={source.thumbnailUrl} alt={source.title ?? "Source thumbnail"} loading="lazy" onError={() => setThumbFailed(true)} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ql-yellow)" }}><Video size={32} strokeWidth={1.5} /></div>
        )}
        {source.clipCount > 0 && (
          <div style={{ position: "absolute", top: 8, right: 8, padding: "3px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, color: "var(--ql-yellow)", background: "rgba(0,0,0,.65)", border: "1px solid rgba(255,213,0,.30)", backdropFilter: "blur(8px)", fontFamily: "var(--ql-font-mono)" }}>
            {source.clipCount} clip{source.clipCount === 1 ? "" : "s"}
          </div>
        )}
        <BookmarkButton overlay bookmarked={source.bookmarkedByActive} busy={bookmarking} onClick={onToggleBookmark} />
      </div>

      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        <div title={source.title ?? source.url} style={{ fontSize: 13, fontWeight: 600, color: "var(--ql-fg)", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {source.title || "(untitled YouTube video)"}
        </div>
        <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {speakers || "—"}
        </div>

        {source.channels.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {source.channels.map((c) => <ChannelTouchBadge key={c.slug} touch={c} />)}
          </div>
        )}

        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <button
            type="button" onClick={onScan} disabled={scanState === "busy"}
            title="Auto-scan this video for this channel — candidates land in your Backlog"
            style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(96,165,250,.30)", background: scanState === "done" ? "rgba(74,222,128,.10)" : "rgba(96,165,250,.08)", color: scanState === "done" ? "#4ADE80" : "#93C5FD", fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: scanState === "busy" ? "default" : "pointer" }}
          >
            {scanState === "busy" ? <Loader2 size={13} className="ql-spin" /> : scanState === "done" ? <Check size={13} strokeWidth={2.4} /> : <ScanSearch size={13} strokeWidth={2} />}
            {scanState === "busy" ? "Scanning…" : scanState === "done" ? "Started" : "Scan"}
          </button>
          <button
            type="button" onClick={onClip}
            title="Start a new clip from this source for this channel"
            style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(255,213,0,.25)", background: "rgba(255,213,0,.08)", color: "var(--ql-yellow)", fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}
          >
            <Send size={12} strokeWidth={2.2} /> Clip
          </button>
          <a href={source.url} target="_blank" rel="noreferrer" title="Open original on YouTube" style={{ width: 36, borderRadius: 10, border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.02)", color: "var(--ql-fg-muted)", display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
            <ExternalLink size={13} strokeWidth={2} />
          </a>
        </div>
      </div>
    </div>
  );
}

function DiscoverEmptyHero(): React.JSX.Element {
  return (
    <div style={{ padding: "60px 24px", textAlign: "center", border: "1px dashed rgba(255,255,255,.08)", borderRadius: 16, background: "rgba(16,18,27,.40)" }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, margin: "0 auto 14px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(255,213,0,.10)", color: "var(--ql-yellow)" }}>
        <Users size={28} strokeWidth={1.5} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ql-fg)", marginBottom: 6 }}>The shared library is empty</div>
      <div style={{ fontSize: 13, maxWidth: 460, margin: "0 auto", lineHeight: 1.5, color: "var(--ql-fg-muted)" }}>
        As soon as any channel clips, scans, or locates a video, it shows up here — a shared pool every channel can scan, clip, or bookmark from.
      </div>
    </div>
  );
}

function EmptyHero(): React.JSX.Element {
  return (
    <div
      style={{
        padding: "60px 24px",
        textAlign: "center",
        border: "1px dashed rgba(255,255,255,.08)",
        borderRadius: 16,
        background: "rgba(16,18,27,.40)",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          margin: "0 auto 14px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,213,0,.10)",
          color: "var(--ql-yellow)",
        }}
      >
        <Video size={28} strokeWidth={1.5} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ql-fg)", marginBottom: 6 }}>
        No sources yet
      </div>
      <div
        style={{
          fontSize: 13,
          maxWidth: 460,
          margin: "0 auto",
          lineHeight: 1.5,
          color: "var(--ql-fg-muted)",
        }}
      >
        Sources appear here automatically after your first clip ships. Each row
        is one unique YouTube podcast — clip the same source a second time and
        it bumps to the top.
      </div>
    </div>
  );
}

function SkeletonGrid(): React.JSX.Element {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 16,
      }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 280,
            borderRadius: 14,
            background:
              "linear-gradient(90deg, rgba(255,255,255,.025) 0%, rgba(255,255,255,.05) 50%, rgba(255,255,255,.025) 100%)",
            backgroundSize: "200% 100%",
            animation: "ql-shimmer 1.4s linear infinite",
          }}
        />
      ))}
    </div>
  );
}
